import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, AlertCircle, SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import {
  GeneratePostRequestPostStyle,
  GeneratePostRequestSpacingStyle,
  GeneratePostRequestHookStyle,
  GeneratePostRequestPerspective,
  GeneratePostRequestHumorLevel,
  GeneratePostRequestAudience,
  GeneratePostRequestEmojiUsage,
} from "@workspace/api-client-react";
import { WritingStyleOptions } from "@/components/writing-style-options";

interface ConflictWarning {
  fields: string[];
  message: string;
}

interface PlatformDesc {
  postStyle?: string;
  hook?: string;
  spacing?: string;
  humor?: string;
  audience?: string;
  emoji?: string;
}

type StyleSuggestion = {
  hook?: GeneratePostRequestHookStyle;
  spacing?: GeneratePostRequestSpacingStyle;
  humor?: GeneratePostRequestHumorLevel;
};

interface VoiceState {
  label: string;
  description: string;
  dominantTone: string;
  exampleContext: string;
}

interface RecentTopic {
  topic: string;
  format: string;
}

interface AdvancedOptionsPanelProps {
  advancedOpen: boolean;
  setAdvancedOpen: (open: boolean) => void;
  advancedCount: number;
  platform: string;
  currentIdea: string;
  platformDesc: PlatformDesc;
  currentSuggestions: StyleSuggestion;
  conflicts: ConflictWarning[];
  voiceStates?: VoiceState[];
  selectedVoiceState: string | undefined;
  setSelectedVoiceState: (v: string | undefined) => void;
  audienceDescription: string;
  setAudienceDescription: (v: string) => void;
  postStyle: GeneratePostRequestPostStyle | undefined;
  setPostStyle: (v: GeneratePostRequestPostStyle | undefined) => void;
  hookStyle: GeneratePostRequestHookStyle | undefined;
  setHookStyle: (v: GeneratePostRequestHookStyle | undefined) => void;
  spacingStyle: GeneratePostRequestSpacingStyle | undefined;
  setSpacingStyle: (v: GeneratePostRequestSpacingStyle | undefined) => void;
  perspective: GeneratePostRequestPerspective | undefined;
  setPerspective: (v: GeneratePostRequestPerspective | undefined) => void;
  humorLevel: GeneratePostRequestHumorLevel | undefined;
  setHumorLevel: (v: GeneratePostRequestHumorLevel | undefined) => void;
  audience: GeneratePostRequestAudience | undefined;
  setAudience: (v: GeneratePostRequestAudience | undefined) => void;
  emojiUsage: GeneratePostRequestEmojiUsage | undefined;
  setEmojiUsage: (v: GeneratePostRequestEmojiUsage | undefined) => void;
  targetWordCount: string;
  setTargetWordCount: (v: string) => void;
  customInstruction: string;
  setCustomInstruction: (v: string) => void;
  blendStyle: string | undefined;
  setBlendStyle: (v: string | undefined) => void;
  recentTopics: RecentTopic[];
  clearRecentContext: () => void;
  clearAdvanced: () => void;
}

export function AdvancedOptionsPanel({
  advancedOpen,
  setAdvancedOpen,
  advancedCount,
  platform,
  currentIdea,
  platformDesc,
  currentSuggestions,
  conflicts,
  voiceStates,
  selectedVoiceState,
  setSelectedVoiceState,
  audienceDescription,
  setAudienceDescription,
  postStyle,
  setPostStyle,
  hookStyle,
  setHookStyle,
  spacingStyle,
  setSpacingStyle,
  perspective,
  setPerspective,
  humorLevel,
  setHumorLevel,
  audience,
  setAudience,
  emojiUsage,
  setEmojiUsage,
  targetWordCount,
  setTargetWordCount,
  customInstruction,
  setCustomInstruction,
  blendStyle,
  setBlendStyle,
  recentTopics,
  clearRecentContext,
  clearAdvanced,
}: AdvancedOptionsPanelProps) {
  const isTwitter = platform === "twitter";
  const isLinkedIn = platform === "linkedin";

  return (
    <div className="mt-4 border-t border-border/60 pt-3">
      <button
        onClick={() => setAdvancedOpen(!advancedOpen)}
        className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors w-full rounded-lg px-2 py-1.5 ring-1 ring-cyan-500/30 shadow-[0_0_8px_0_rgba(6,182,212,0.18)] hover:ring-cyan-500/50 hover:shadow-[0_0_12px_0_rgba(6,182,212,0.28)]"
      >
        <SlidersHorizontal className="w-3.5 h-3.5 text-cyan-500/70" />
        Advanced options
        {advancedCount > 0 && (
          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-bold">
            {advancedCount}
          </span>
        )}
        {advancedOpen ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
      </button>

      <AnimatePresence initial={false}>
        {advancedOpen && (
          <motion.div
            key="advanced-post"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4 space-y-4">
              {/* Audience Mirror */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Audience mirror</label>
                  {audienceDescription.trim() && (
                    <button onClick={() => setAudienceDescription("")} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                  )}
                </div>
                <input
                  type="text"
                  value={audienceDescription}
                  onChange={(e) => setAudienceDescription(e.target.value.slice(0, 200))}
                  placeholder="Who are you writing for? e.g. startup founders skeptical of AI"
                  className="w-full text-xs bg-background border border-border rounded-xl p-3 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                />
                <p className="text-[10px] text-muted-foreground mt-1">AI sharpens emphasis and hooks to resonate with this reader without changing your voice</p>
              </div>

              {/* Voice State Selector */}
              {voiceStates && voiceStates.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Voice Mode</label>
                    {selectedVoiceState && (
                      <button onClick={() => setSelectedVoiceState(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 mb-2 leading-relaxed">Select which mode of your writing voice to channel in this post.</p>
                  <div className="flex flex-wrap gap-1.5">
                    {voiceStates.map((vs) => (
                      <button
                        key={vs.label}
                        onClick={() => setSelectedVoiceState(selectedVoiceState === vs.label ? undefined : vs.label)}
                        title={`${vs.description} — Best for: ${vs.exampleContext}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          selectedVoiceState === vs.label
                            ? "bg-primary/15 border-primary/50 text-primary"
                            : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
                        }`}
                      >
                        {vs.label}
                      </button>
                    ))}
                  </div>
                  {selectedVoiceState && (() => {
                    const active = voiceStates.find(s => s.label === selectedVoiceState);
                    return active ? (
                      <p className="text-[10px] text-muted-foreground mt-2 pl-1 leading-relaxed italic">{active.description}</p>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Conflict warnings */}
              {conflicts.length > 0 && (
                <div className="space-y-1.5">
                  {conflicts.map((c, i) => (
                    <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-xl bg-amber-500/8 border border-amber-500/25 text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <p className="text-[10px] leading-relaxed">{c.message}</p>
                    </div>
                  ))}
                </div>
              )}

              <WritingStyleOptions
                platform={platform}
                platformDesc={platformDesc}
                currentIdea={currentIdea}
                currentSuggestions={currentSuggestions}
                postStyle={postStyle}
                setPostStyle={setPostStyle}
                hookStyle={hookStyle}
                setHookStyle={setHookStyle}
                spacingStyle={spacingStyle}
                setSpacingStyle={setSpacingStyle}
                perspective={perspective}
                setPerspective={setPerspective}
                humorLevel={humorLevel}
                setHumorLevel={setHumorLevel}
                audience={audience}
                setAudience={setAudience}
                emojiUsage={emojiUsage}
                setEmojiUsage={setEmojiUsage}
              />


              {/* Target Word Count */}
              {isTwitter ? (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-sky-500/8 border border-sky-500/20 text-sky-500">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <p className="text-[10px] leading-relaxed">Twitter/X has a 280-character limit — word count is automatically managed. Focus on the hook and style instead.</p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="target-word-count" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Target Word Count</label>
                    {targetWordCount.trim() && (
                      <button onClick={() => setTargetWordCount("")} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                    )}
                  </div>
                  <input
                    id="target-word-count"
                    type="number"
                    min={5}
                    max={2000}
                    value={targetWordCount}
                    onChange={(e) => setTargetWordCount(e.target.value)}
                    placeholder="e.g. 150"
                    className="w-full text-xs bg-background border border-border rounded-xl p-3 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">The AI will aim for exactly this many words (±10%)</p>
                </div>
              )}

              {/* Custom instruction */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="custom-instruction" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Any special vibe for this post?</label>
                  {customInstruction.trim() && (
                    <button onClick={() => setCustomInstruction("")} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                  )}
                </div>
                <textarea
                  id="custom-instruction"
                  data-testid="custom-instruction-textarea"
                  value={customInstruction}
                  onChange={(e) => setCustomInstruction(e.target.value.slice(0, 300))}
                  placeholder="e.g. be sarcastic, ask the reader lots of questions, keep it blunt…"
                  rows={2}
                  className="w-full text-xs bg-background border border-border rounded-xl p-3 text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                />
                <p className="text-[10px] text-muted-foreground mt-1 text-right">{customInstruction.length}/300</p>
              </div>

              {/* Voice Blend */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Blend style</label>
                  {blendStyle && (
                    <button onClick={() => setBlendStyle(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: "ted-talk", label: "TED Talk" },
                    { id: "documentary-narrator", label: "Documentary" },
                    { id: "op-ed-columnist", label: "Op-ed" },
                    { id: "mentor-letter", label: "Mentor letter" },
                    { id: "investigative-journalist", label: "Investigative" },
                    { id: "comedian-storyteller", label: "Stand-up story" },
                    { id: "startup-founder-post", label: "Founder's story" },
                    { id: "coach-framework", label: "Coach's framework" },
                    { id: "confessional-diary", label: "Confessional" },
                    { id: "poet-prose", label: "Prose poet" },
                  ].map((archetype) => (
                    <button
                      key={archetype.id}
                      onClick={() => setBlendStyle(blendStyle === archetype.id ? undefined : archetype.id)}
                      className={`px-2.5 py-1 text-[11px] rounded-lg border transition-all font-medium ${
                        blendStyle === archetype.id
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                      }`}
                    >
                      {archetype.label}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Borrows structural architecture from this format while keeping your vocabulary and cadence</p>
              </div>

              {/* Recent Context */}
              {recentTopics.length > 0 && (
                <div className="pt-1">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground/70">
                      <span className="font-semibold text-muted-foreground">Context:</span>{" "}
                      {recentTopics[0].topic.length > 40
                        ? recentTopics[0].topic.slice(0, 40) + "…"
                        : recentTopics[0].topic}
                      {recentTopics.length > 1 && ` +${recentTopics.length - 1} more`}
                    </p>
                    <button
                      onClick={clearRecentContext}
                      className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors"
                    >
                      Clear context
                    </button>
                  </div>
                </div>
              )}

              {advancedCount > 0 && (
                <button
                  onClick={clearAdvanced}
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                >
                  Clear all advanced settings
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
